import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { NbToastrService } from '@nebular/theme';
import { SettingService } from 'src/app/shared/services/setting.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';

interface KeywordSuggestion {
  keyword: string;
  search_volume: number;
  competition: number;
  competition_level: string;
  cpc: number;
  high_top_of_page_bid: number;
  keyword_difficulty: number;
  low_top_of_page_bid: number;
  monthly_searches: any[];
  selected?: boolean;
}

@Component({
  selector: 'app-keyword-suggestion',
  templateUrl: './keyword-suggestion.component.html',
  styleUrls: ['./keyword-suggestion.component.scss']
})
export class KeywordSuggestionComponent implements OnInit {
  keywordControl: FormControl;
  defaultKeyword: string = '';
  keywordSource: string = '';
  isLoading: boolean = false;
  isTableLoading: boolean = false;
  suggestions: KeywordSuggestion[] = [];
  filteredSuggestions: KeywordSuggestion[] = [];
  displayedColumns: string[] = ['select', 'keyword', 'search_volume', 'competition', 'competition_level', 'cpc', 'keyword_difficulty'];
  
  pageSize: number = 25;
  pageIndex: number = 0;
  hasSearched: boolean = false;
  
  selectedKeywords: KeywordSuggestion[] = [];
  
  sortColumns: Array<{column: string, direction: 'asc' | 'desc'}> = [];
  originalFilteredOrder: KeywordSuggestion[] = [];
  
  get paginatedSuggestions(): KeywordSuggestion[] {
    const startIndex = this.pageIndex * this.pageSize;
    return this.filteredSuggestions.slice(startIndex, startIndex + this.pageSize);
  }

  get allSelected(): boolean {
    return this.paginatedSuggestions.length > 0 && this.paginatedSuggestions.every(s => s.selected);
  }

  get someSelected(): boolean {
    return this.paginatedSuggestions.some(s => s.selected) && !this.allSelected;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<KeywordSuggestionComponent>,
    private dialog: MatDialog,
    private http: HttpClient,
    private toast: NbToastrService,
    private settingService: SettingService
  ) {
    if (this.data) {
      this.defaultKeyword = this.data.defaultKeyword || '';
      this.keywordSource = this.data.keywordSource || '';
    }
  }

  ngOnInit(): void {
    this.keywordControl = new FormControl(this.defaultKeyword, [Validators.required]);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onGetSuggestions(): Promise<void> {
    if (!this.keywordControl.valid) {
      return;
    }

    try {
      this.isLoading = true;
      this.suggestions = [];
      this.pageIndex = 0;
      this.hasSearched = true;

      var endpoint = "";
      const settingResp = await this.settingService.getSettingByKeyAndGroup("KEYWORD_SUGGESTION_URL", "AUTOMATION").toPromise();
      if (settingResp?.result?.value) {
        endpoint = settingResp.result.value;
      }

      if (!endpoint) {
        this.toast.danger("Setting KEYWORD_SUGGESTION_URL is not configured");
        return;
      }

      const keyword = this.keywordControl.value.trim();
      const encodedKeyword = encodeURIComponent(keyword).replace(/%20/g, '+');
      endpoint += `?keyword=${encodedKeyword}&location_name=United+States&language_code=en`;

      const resp: any = await this.http.post(endpoint, {}).toPromise();
      
      if (resp?.success && resp?.keywords && Array.isArray(resp.keywords)) {
        this.suggestions = resp.keywords.map(k => ({ ...k, selected: false }));
        this.filteredSuggestions = [...this.suggestions];
        this.originalFilteredOrder = [...this.suggestions];
      } else {
        this.toast.warning("No suggestions found");
      }
    }
    catch (ex) {
      console.error(ex);
      this.toast.danger("Failed to get keyword suggestions");
    }
    finally {
      this.isLoading = false;
    }
  }

  async onPageChange(event: any): Promise<void> {
    this.isTableLoading = true;
    const tempList = [...this.filteredSuggestions];
    this.filteredSuggestions = [];
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.filteredSuggestions = tempList;
    this.isTableLoading = false;
  }

  toggleSelectAll(): void {
    const newValue = !this.allSelected;
    this.paginatedSuggestions.forEach(s => s.selected = newValue);
    this.updateSelectedKeywords();
  }

  toggleSelection(keyword: KeywordSuggestion): void {
    keyword.selected = !keyword.selected;
    this.updateSelectedKeywords();
  }

  updateSelectedKeywords(): void {
    this.selectedKeywords = this.filteredSuggestions.filter(s => s.selected);
  }

  async sortBy(column: string): Promise<void> {
    const existingSort = this.sortColumns.find(s => s.column === column);
    
    if (existingSort) {
      if (existingSort.direction === 'asc') {
        existingSort.direction = 'desc';
      } else {
        this.sortColumns = this.sortColumns.filter(s => s.column !== column);
      }
    } else {
      this.sortColumns.push({ column, direction: 'asc' });
    }
    
    this.isTableLoading = true;
    const tempList = [...this.filteredSuggestions];
    this.filteredSuggestions = [];
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.filteredSuggestions = tempList;
    this.applySort();
    this.isTableLoading = false;
  }

  getSortState(column: string): 'asc' | 'desc' | null {
    const sort = this.sortColumns.find(s => s.column === column);
    return sort ? sort.direction : null;
  }

  getSortPriority(column: string): number {
    const index = this.sortColumns.findIndex(s => s.column === column);
    return index >= 0 ? index + 1 : 0;
  }

  applySort(): void {
    if (this.sortColumns.length === 0) {
      this.filteredSuggestions = [...this.originalFilteredOrder];
      return;
    }

    this.filteredSuggestions.sort((a, b) => {
      for (const sort of this.sortColumns) {
        let aVal = a[sort.column];
        let bVal = b[sort.column];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }


  async resetTable(): Promise<void> {
    this.isTableLoading = true;
    this.filteredSuggestions = [];
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.sortColumns = [];
    this.selectedKeywords = [];
    this.filteredSuggestions = [...this.originalFilteredOrder];
    this.filteredSuggestions.forEach(s => s.selected = false);
    this.pageIndex = 0;
    this.isTableLoading = false;
  }

  async addAsPrimary(): Promise<void> {
    if (this.selectedKeywords.length === 0) {
      this.toast.warning('Please select at least one keyword');
      return;
    }

    const count = this.selectedKeywords.length;
    const message = `Do you wish to add ${count} keyword(s) as primary keyword? This will replace the current primary keyword.`;
    
    const confirmDialog = this.dialog.open(ConfirmModalComponent, {
      data: { message }
    });

    const result = await confirmDialog.afterClosed().toPromise();
    if (result) {
      this.dialogRef.close({ type: 'primary', keywords: this.selectedKeywords });
    }
  }

  async addAsSecondary(): Promise<void> {
    if (this.selectedKeywords.length === 0) {
      this.toast.warning('Please select at least one keyword');
      return;
    }

    const count = this.selectedKeywords.length;
    const message = `Do you wish to add ${count} keyword(s) as secondary keyword?`;
    
    const confirmDialog = this.dialog.open(ConfirmModalComponent, {
      data: { message }
    });

    const result = await confirmDialog.afterClosed().toPromise();
    if (result) {
      this.dialogRef.close({ type: 'secondary', keywords: this.selectedKeywords });
    }
  }
}
